import { Roster, Matchup } from '../types/fantasy';
import { playerDataService } from './playerDataService';

export interface PodcastPromptData {
  userTeam: any;
  userMatchup: any;  // Only the user's matchup
  opponentTeam: any;  // Only the opponent's data
  prompt: string;
  team_info: string; // Team info as formatted text string
}

function formatTeamInfoAsText(myTeamName: string, leagueMatchups: any[], leagueTeams: any[]): string {
  let textOutput = '';
  
  // Header
  textOutput += `FANTASY LEAGUE INFORMATION\n`;
  textOutput += `==========================\n\n`;
  
  // My Team
  textOutput += `My Team: ${myTeamName}\n\n`;
  
  // League Teams Summary
  textOutput += `LEAGUE TEAMS (${leagueTeams.length} teams):\n`;
  textOutput += `-`.repeat(40) + '\n';
  
  leagueTeams.forEach((team, index) => {
    textOutput += `${index + 1}. ${team.teamName}\n`;
    
    // Group players by position
    const positions: { [key: string]: any[] } = {};
    team.roster.forEach((player: any) => {
      if (!positions[player.position]) {
        positions[player.position] = [];
      }
      positions[player.position].push(player);
    });
    
    // Display starters and bench separately
    const starters = team.roster.filter((p: any) => p.status === 'starter');
    const bench = team.roster.filter((p: any) => p.status === 'bench');
    
    if (starters.length > 0) {
      textOutput += `   Starters: ${starters.map((p: any) => `${p.name} (${p.position})`).join(', ')}\n`;
    }
    if (bench.length > 0) {
      textOutput += `   Bench: ${bench.map((p: any) => `${p.name} (${p.position})`).join(', ')}\n`;
    }
    textOutput += '\n';
  });
  
  // League Matchups
  if (leagueMatchups && leagueMatchups.length > 0) {
    textOutput += `\nLEAGUE MATCHUPS:\n`;
    textOutput += `-`.repeat(40) + '\n';
    
    leagueMatchups.forEach((matchup, index) => {
      textOutput += `Week ${matchup.week} Matchup ${index + 1}:\n`;
      textOutput += `  ${matchup.team1.name} (${matchup.team1.points} pts) vs ${matchup.team2.name} (${matchup.team2.points} pts)\n`;
      
      // Determine winner if game is complete
      if (matchup.team1.points > 0 || matchup.team2.points > 0) {
        const winner = matchup.team1.points > matchup.team2.points ? matchup.team1.name : matchup.team2.name;
        textOutput += `  Winner: ${winner}\n`;
      }
      textOutput += '\n';
    });
  }
  
  return textOutput;
}

export function generatePodcastPrompt(
  selectedTeam: Roster,
  allRosters: Roster[],
  allMatchups: Matchup[],  // Now using all matchups instead of selected
  selectedHost: string,
  userInfo?: any
): PodcastPromptData {
  
  // Debug logging
  console.log('Selected team for prompt generation:', {
    teamName: selectedTeam.teamName,
    userId: selectedTeam.userId,
    playerCount: selectedTeam.players?.length || 0,
    firstPlayer: selectedTeam.players?.[0] || null
  });

  // Validate roster data
  if (!selectedTeam.players || selectedTeam.players.length === 0) {
    console.error('ERROR: No players found in selected team!', selectedTeam);
    return {
      userTeam: {
        name: selectedTeam.teamName || 'Unknown Team',
        roster: []
      },
      userMatchup: null,
      opponentTeam: null,
      prompt: `Error: No roster data available for ${selectedTeam.teamName || 'this team'}. Please try reloading the league data.`,
      team_info: `ERROR: No roster data available for ${selectedTeam.teamName || 'this team'}. Please reload the league data.`
    };
  }

  // Process the user's team roster
  const userTeamData = {
    name: selectedTeam.teamName || 'Unknown Team',
    roster: selectedTeam.players.map(player => {
      // Get enriched player data from JSON files
      const enrichedData = playerDataService.getPlayerInfo(player.name);
      
      console.log(`Processing player: ${player.name} (${player.position}) - Enriched data:`, !!enrichedData);
      
      return {
        name: player.name || 'Unknown Player',
        position: player.position || 'UNK',
        status: selectedTeam.starters?.includes(player.id) ? 'starter' : 'bench',
        team: enrichedData?.team || player.team || 'FA',
        number: enrichedData?.number,
        week1Analysis: enrichedData?.week1Analysis,
        projectedPoints: enrichedData?.projectedPoints,
        adp: enrichedData?.adp,
        week1Opponent: enrichedData?.week1Opponent,
        // Include all available player info from JSON
        fullDetails: enrichedData || null
      };
    })
  };

  // Find the user's specific matchup ONLY
  const userMatchup = allMatchups.find(m => 
    m.team1.userId === selectedTeam.userId || m.team2.userId === selectedTeam.userId
  );

  // Get the opponent's data if matchup exists
  let opponentTeamData: any = null;
  let formattedUserMatchup: any = null;
  
  if (userMatchup) {
    // Determine which team is the opponent
    const isTeam1 = userMatchup.team1.userId === selectedTeam.userId;
    const opponentUserId = isTeam1 ? userMatchup.team2.userId : userMatchup.team1.userId;
    
    // Find the opponent's roster
    const opponentRoster = allRosters.find(r => r.userId === opponentUserId);
    
    if (opponentRoster) {
      // Check if opponent has players
      if (!opponentRoster.players || opponentRoster.players.length === 0) {
        console.warn('WARNING: Opponent roster has no players!', opponentRoster);
        opponentTeamData = {
          teamId: opponentRoster.userId,
          teamName: opponentRoster.teamName || `Team ${opponentRoster.userId}`,
          totalPoints: opponentRoster.totalPoints || 0,
          roster: []
        };
      } else {
        opponentTeamData = {
          teamId: opponentRoster.userId,
          teamName: opponentRoster.teamName || `Team ${opponentRoster.userId}`,
          totalPoints: opponentRoster.totalPoints || 0,
          roster: opponentRoster.players.map(player => {
            const enrichedData = playerDataService.getPlayerInfo(player.name);
            return {
              name: player.name || 'Unknown Player',
              position: player.position || 'UNK',
              status: opponentRoster.starters?.includes(player.id) ? 'starter' : 'bench',
              team: enrichedData?.team || player.team || 'FA',
              number: enrichedData?.number,
              week1Analysis: enrichedData?.week1Analysis,
              projectedPoints: enrichedData?.projectedPoints,
              adp: enrichedData?.adp,
              week1Opponent: enrichedData?.week1Opponent,
              fullDetails: enrichedData || null
            };
          })
        };
      }
    }

    // Format the user's matchup for the prompt
    formattedUserMatchup = {
      week: userMatchup.week,
      userTeamName: selectedTeam.teamName || 'Your Team',
      opponentTeamName: opponentTeamData?.teamName || 'Opponent',
      userPoints: isTeam1 ? userMatchup.team1.points || 0 : userMatchup.team2.points || 0,
      opponentPoints: isTeam1 ? userMatchup.team2.points || 0 : userMatchup.team1.points || 0
    };
  }

  // Generate the prompt with ONLY the user's matchup
  const prompt = generateAnalysisPrompt(
    userTeamData,
    formattedUserMatchup,
    opponentTeamData,
    selectedHost,
    userInfo?.name || 'the caller',
    allMatchups
  );

  // Simplified league rosters - only basic info
  const allLeagueRosters = (allRosters || []).map(roster => ({
    teamName: roster.teamName || 'Unknown Team',
    teamId: roster.userId,
    roster: (roster.players || []).map(player => ({
      name: player.name || 'Unknown Player',
      position: player.position || 'UNK',
      status: (roster.starters || []).includes(player.id) ? 'starter' : 'bench'
    }))
  }));

  // Format all league matchups - handle platforms with limited matchup support (like Yahoo)
  const allLeagueMatchups = (allMatchups || []).map(matchup => ({
    week: matchup.week || 1,
    team1: {
      name: matchup.team1?.teamName || 'Team 1',
      userId: matchup.team1?.userId || 'unknown',
      points: matchup.team1?.points || 0
    },
    team2: {
      name: matchup.team2?.teamName || 'Team 2', 
      userId: matchup.team2?.userId || 'unknown',
      points: matchup.team2?.points || 0
    }
  }));

  // Convert team_info to readable text format instead of JSON
  const team_info_string = formatTeamInfoAsText(userTeamData.name, allLeagueMatchups, allLeagueRosters);

  return {
    userTeam: userTeamData,
    userMatchup: formattedUserMatchup,  // Only the user's matchup
    opponentTeam: opponentTeamData,  // Only the opponent's data
    prompt,
    team_info: team_info_string  // Return as string
  };
}

function generateAnalysisPrompt(
  userTeam: any,
  userMatchup: any,
  opponentTeam: any,
  host: string,
  callerName: string,
  allMatchups: Matchup[]
): string {
  
  // Format the user's roster with Week 1 analysis
  const userRosterStr = userTeam.roster
    .map((p: any) => {
      const analysis = p.week1Analysis ? ` - ${p.week1Analysis.substring(0, 100)}...` : '';
      return `  - ${p.name} (${p.position}) - ${p.status}${analysis}`;
    })
    .join('\n');

  // Format opponent's roster if exists
  let matchupAnalysis = '';
  if (userMatchup && opponentTeam) {
    const opponentStarters = opponentTeam.roster
      .filter((p: any) => p.status === 'starter')
      .map((p: any) => `    - ${p.name} (${p.position})`)
      .join('\n');
    
    const userStarters = userTeam.roster
      .filter((p: any) => p.status === 'starter')
      .map((p: any) => `    - ${p.name} (${p.position})`)
      .join('\n');

    matchupAnalysis = `
${callerName}'s matchup is against "${opponentTeam.teamName}" in Week ${userMatchup.week}.

Your Starting Lineup:
${userStarters}

${opponentTeam.teamName}'s Starting Lineup:
${opponentStarters}

Current Score: ${userTeam.name} (${userMatchup.userPoints}) vs ${opponentTeam.teamName} (${userMatchup.opponentPoints})`;
  } else {
    matchupAnalysis = `No matchup found for this week. Focus on the roster analysis.`;
  }

  const prompt = `You are analyzing ${userTeam.name} in his fantasy league, you look at the league matchups and the league rosters and make judgements based on that.

You are ${host}, a fantasy football podcast host having a conversation with ${callerName}. 

The caller's team is "${userTeam.name}". Here's their complete roster with Week 1 analysis:

${userRosterStr}

${matchupAnalysis}

Analyze ${callerName}'s team and their specific matchup. Consider:
- Key player matchups and Week 1 performance expectations
- Position group advantages based on the Week 1 analysis data
- Injury concerns or boom/bust players
- Your prediction for who wins this specific matchup and why
- How their team compares to other teams in the league based on all available roster data

You have access to all league rosters and can provide comprehensive analysis comparing ${userTeam.name} to the rest of the league. ${allMatchups && allMatchups.length > 0 ? 'You also have complete matchup data for deeper analysis.' : 'Note: Detailed matchup data may be limited depending on the fantasy platform used.'}

Keep the tone conversational and engaging, as if you're on a live podcast. Use the Week 1 analysis data to make specific, insightful comments about players.`;

  return prompt;
}
